import { useRef, useState } from 'react'
import classNames from 'classnames'
import { ShoppingAddToBag } from 'src/components/Icons/ShoppingAddToBagIcon'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useMobile } from 'src/hooks/useMobile'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import ModalGiftCard from 'src/components/GiftCard/components/Modals/ModalGiftCard'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { isPreSale } from 'src/utils/pdp/isPreSale'
import ModalBlockAddToCart from 'src/components/common/Modal/ModalBlockAddToCart'

import type { ProductItem } from '../types/productItem'
import type { Product } from '../types/product'
import {
  useAddToCartLogic,
  useSkuSelectionModal,
  useBuyButtonHandler,
  useClearCartHandler,
} from './hooks'
import { isGiftCard } from './utils/giftCardUtils'

interface ProductAddToCartProps {
  product: Product
  currentSku: ProductItem
  Images: ProductItem['images']
  origin?: 'cart-feedback' | 'product-card'
  title?: string
  customFnOnAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  eventSource?: string
  addLocation?: string
}

const DefaultCta = ({
  handleBuyButton,
  handleClearCartAndContinue,
  openModalGiftCard,
  setOpenModalGiftCard,
  openModalPreSale,
  setOpenModalPreSale,
  label,
}: {
  handleBuyButton: (e: React.MouseEvent<HTMLButtonElement>) => void
  handleClearCartAndContinue: (e: React.MouseEvent<HTMLButtonElement>) => void
  openModalGiftCard: boolean
  setOpenModalGiftCard: React.Dispatch<React.SetStateAction<boolean>>
  setOpenModalPreSale: React.Dispatch<React.SetStateAction<boolean>>
  openModalPreSale: boolean
  label?: string
}) => {
  return (
    <>
      <button
        className={classNames(
          'product-card-add-to-cart p-2 rounded-round absolute bottom-2 right-2 bg-restructure-background-primary-inverted flex items-center justify-center gap-2 restructure-tablet:hidden restructure-tablet:py-[13.5px] restructure-tablet:px-0 restructure-tablet:left-2 restructure-tablet:border restructure-tablet:border-gray restructure-tablet:rounded-none restructure-tablet:w-[calc(100%-16px)] restructure-tablet:h-12'
        )}
        onClick={(e) => handleBuyButton(e)}
        aria-label={label ?? ''}
      >
        <ShoppingAddToBag />
        <span className="hidden text-body-sm font-medium text-restructure-primary-inverted restructure-tablet:inline-block">
          Adicionar ao carrinho
        </span>
      </button>

      {/* Modal: Cartão Presente no carrinho + adicionar Produto diferente */}
      <ModalGiftCard
        openModalGiftCard={openModalGiftCard}
        setOpenModalGiftCard={setOpenModalGiftCard}
        mainMessage="Não é possível comprar um Cartão Presente junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para adicionar este produto ao carrinho?"
        buttonLabel="Limpar e adicionar ao carrinho"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndContinue}
      />
      <ModalBlockAddToCart
        openModal={openModalPreSale}
        setOpenModal={setOpenModalPreSale}
        mainMessage="Não é possível comprar um produto em pré venda junto com outros produtos."
        subMessage="Deseja limpar seu carrinho para continuar com a compra?"
        buttonLabel="Limpar carrinho e continuar"
        cancelLabel="Cancelar"
        onClearCart={handleClearCartAndContinue}
      />
    </>
  )
}

const CartFeedbackCta = ({
  handleBuyButton,
}: {
  handleBuyButton: (e: React.MouseEvent<HTMLButtonElement>) => void
}) => {
  return (
    <button
      className="flex items-center justify-center bg-primaryBlue min-w-8 min-h-8 w-8 h-8 rounded-full"
      onClick={(e) => handleBuyButton(e)}
      type="button"
      aria-label="Adicionar produto ao carrinho"
    >
      <ShoppingAddToBag width={14} height={14} />
    </button>
  )
}

export function ProductAddToCart({
  product,
  currentSku,
  Images,
  origin = 'product-card',
  eventSource,
  addLocation,
  title,
  customFnOnAdd,
}: ProductAddToCartProps) {
  const {
    orderForm: { items },
    isLoading,
    clearCart,
  } = useCheckout()

  const { pageData } = useGlobalContext()

  const { isMobile } = useMobile()
  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(currentSku)

  const [openModalPreSale, setOpenModalPreSale] = useState(false)
  const [openModalGiftCard, setOpenModalGiftCard] = useState(false)
  const cartWasClearedRef = useRef(false)

  const hasMultipleItems = product.items.length > 1
  const currentProductIsGiftCard = isGiftCard(product.productId)
  const cartContainsGiftCard = items?.some((item) =>
    isGiftCard(item?.productId)
  )

  const cartContainsPreSaleItem = items?.some((item) =>
    isPreSale(item.preSaleDate)
  )

  const { addToCartFn } = useAddToCartLogic({
    product,
    currentSku,
    cartWasClearedRef,
    title,
    customFnOnAdd,
    eventSource,
    addLocation,
  })

  const { openSkuSelectionModal } = useSkuSelectionModal({
    product,
    currentSku,
    Images,
    isLoading,
    addToCartFn,
    isMobile,
    standardObject,
    productObject,
  })

  const handleBuyButton = useBuyButtonHandler({
    currentSku,
    hasMultipleItems,
    currentProductIsGiftCard,
    cartContainsGiftCard,
    cartContainsPreSaleItem,
    standardObject,
    productObject,
    openSkuSelectionModal,
    addToCartFn,
    setOpenModalGiftCard,
    setOpenModalPreSale,
    slug: product.linkText,
  })

  const handleClearCartAndContinue = useClearCartHandler({
    hasMultipleItems,
    currentSku,
    cartWasClearedRef,
    clearCart,
    setOpenModalGiftCard,
    setOpenModalPreSale,
    openSkuSelectionModal,
    addToCartFn,
    standardObject,
    productObject,
  })

  return origin === 'product-card' ? (
    <DefaultCta
      handleBuyButton={handleBuyButton}
      handleClearCartAndContinue={handleClearCartAndContinue}
      openModalPreSale={openModalPreSale}
      setOpenModalPreSale={setOpenModalPreSale}
      openModalGiftCard={openModalGiftCard}
      setOpenModalGiftCard={setOpenModalGiftCard}
      label={`Adicionar ${currentSku.name} ao carrinho`}
    />
  ) : (
    <CartFeedbackCta handleBuyButton={handleBuyButton} />
  )
}
